// Complete a form — the Forms landing (round 28, ZT's shape).
//
// The published forms THEMSELVES, tappable: one tap from opening the app to
// starting a prestart. ZT tried a hub of cards first and retreated — the
// person opening this page at 6:45am wants the form, not a menu about forms.
// Submissions and Form templates are secondary links beneath the list — the
// same three sub-sections the sidebar names since round 37.
//
// Role-shaped without a role check: the forms list answers every role, and
// the templates link is hidden from workers as NAVIGATION (the server refuses
// the route independently, like the nav config).
//
// OFFLINE (round 42): the list comes from the phone's saved copy when there
// is no signal, and "On this phone" shows every form finished here that has
// not reached Dayboard yet, with where it is up to. A form that is safe on
// the phone and one that is lost must never look the same.
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { formFill, type SubmissionListItem } from '../formsApi';
import { useSession } from '../session';
import { ownerKey } from '../offline/localForm';
import { useFormsPack } from '../offline/pack';
import { SEND_STATE_LABEL, sendState, useLocalForms } from '../offline/runner';
import { formatMomentAu } from '../lib/dates';
import { CardList, EmptyState, LinkedRow, PageHeader, RowCard } from '../components/ui';
import { SortHeader } from '../components/ui/SortHeader';
import { sortRows, useSort } from '../lib/sortRows';

type SortKey = 'name' | 'target';

/** What a form is filled in against, said the way the picker will ask it. */
const LINK_HINT: Record<string, string> = {
  job: 'Filled in against a job',
  asset: 'Filled in against a machine',
  any: 'On its own',
};

export function FormsHomePage() {
  const { user, tenant } = useSession();
  const owner = user && tenant ? ownerKey(tenant.id, user.id) : null;
  const packState = useFormsPack(owner);
  const { forms: onPhone } = useLocalForms(owner);
  const [drafts, setDrafts] = useState<SubmissionListItem[]>([]);
  const location = useLocation();
  const queuedId = (location.state as { queued?: string } | null)?.queued ?? null;
  const { sortKey, sortDir, onSort } = useSort<SortKey>('name');

  useEffect(() => {
    // The unfinished strip: a form abandoned mid-fill is one tap away again.
    // A field worker's list is their own (server-scoped); a manager sees the
    // company's, which is honest — each row names who started it.
    void formFill.list({ status: 'draft', perPage: 5 }).then(
      (result) => setDrafts(result.submissions),
      () => undefined // the strip is a convenience; the landing loads without it
    );
  }, []);

  // Finished here, not yet there: Submit was pressed, or the server said no.
  const waiting = onPhone.filter((f) => f.submitAt !== null || f.problem !== null || f.problems.length > 0);
  // Unfinished on this phone. The server's list may name the same form; the
  // phone's copy is the one being worked on, so it wins and is listed once.
  const phoneDrafts = onPhone.filter((f) => !waiting.includes(f));
  const phoneIds = new Set(onPhone.map((f) => f.id));
  const serverDrafts = drafts.filter((d) => !phoneIds.has(d.id));

  const manager = user?.role === 'admin' || user?.role === 'manager';
  const pack = packState.pack;
  const templates = useMemo(
    () =>
      sortRows(
        pack?.forms ?? [],
        (t) => (sortKey === 'name' ? t.name : LINK_HINT[t.linkKind] ?? t.linkKind),
        sortDir
      ),
    [pack, sortKey, sortDir]
  );

  return (
    <>
      <PageHeader
        title="Complete a form"
        subtitle="Pick a form to fill in: prestarts, checklists and sign-offs."
      />

      {queuedId && waiting.some((f) => f.id === queuedId) && (
        <div className="banner-info mb-4" role="status">
          <strong>Saved on this phone.</strong> It sends by itself the next time Dayboard is
          open with signal. It is listed under On this phone until it goes.
        </div>
      )}

      {packState.error && (
        <div className="banner-error mb-4" role="alert">
          {packState.error}
        </div>
      )}

      {packState.fromPhone && packState.savedAt && (
        <p className="hint mb-4">
          No signal right now, so this is this phone's copy of your forms from{' '}
          {formatMomentAu(packState.savedAt)}. You can still fill them in.
        </p>
      )}

      {waiting.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-base font-semibold text-gray-900">On this phone</h2>
          <ul className="m-0 list-none space-y-1 p-0">
            {waiting.map((f) => {
              const state = sendState(f);
              return (
                <li key={f.id}>
                  <Link to={`/app/forms/fill?draft=${f.id}`} className="table-link text-sm">
                    {f.templateName}
                  </Link>{' '}
                  <span
                    className={
                      state === 'attention' ? 'text-sm text-red-700' : 'text-sm text-gray-600'
                    }
                  >
                    {[
                      f.aboutLabel,
                      f.submitAt ? `submitted ${formatMomentAu(f.submitAt)}` : null,
                      SEND_STATE_LABEL[state],
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {pack && templates.length === 0 ? (
        <EmptyState
          title="Nothing to fill in yet"
          body={
            manager
              ? 'Build a form and publish it, and it shows up here for everyone.'
              : 'When the office publishes a form, it shows up here.'
          }
          action={
            manager ? (
              <Link to="/app/forms/templates" className="btn-primary btn-sm">
                Build a form
              </Link>
            ) : undefined
          }
        />
      ) : (
        <>
          <CardList>
            {templates.map((t) => (
              <RowCard key={t.id} to={`/app/forms/fill/${t.id}`} title={t.name}>
                {t.description && <div>{t.description}</div>}
                <div>{LINK_HINT[t.linkKind]}</div>
              </RowCard>
            ))}
          </CardList>
          <div className="table-wrap max-sm:hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader label="Form" sortKey="name" current={sortKey} dir={sortDir} onSort={onSort} />
                  <SortHeader label="Filled in against" sortKey="target" current={sortKey} dir={sortDir} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <LinkedRow key={t.id} to={`/app/forms/fill/${t.id}`}>
                    <td className="font-medium">
                      <Link to={`/app/forms/fill/${t.id}`} className="table-link">
                        {t.name}
                      </Link>
                      {t.description && <div className="table-muted font-normal">{t.description}</div>}
                    </td>
                    <td className="table-muted">{LINK_HINT[t.linkKind]}</td>
                  </LinkedRow>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {phoneDrafts.length + serverDrafts.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-base font-semibold text-gray-900">Unfinished</h2>
          <ul className="m-0 list-none space-y-1 p-0">
            {phoneDrafts.map((f) => (
              <li key={f.id}>
                <Link to={`/app/forms/fill?draft=${f.id}`} className="table-link text-sm">
                  {f.templateName}
                </Link>{' '}
                <span className="text-sm text-gray-600">
                  {[f.aboutLabel, `started ${new Date(f.createdAt).toLocaleDateString('en-AU')}`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
            {serverDrafts.map((d) => (
              <li key={d.id}>
                <Link to={`/app/forms/fill?draft=${d.id}`} className="table-link text-sm">
                  {d.templateName}
                </Link>{' '}
                <span className="text-sm text-gray-600">
                  {/* Who started it: a manager's strip shows the whole
                      company's, and a nameless unfinished form is a question
                      nobody can act on. */}
                  {[
                    d.startedByName,
                    d.jobNumber ?? (d.kind === 'asset' ? 'Machine' : null),
                    `started ${new Date(d.createdAt).toLocaleDateString('en-AU')}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-8 grid gap-3 sm:grid-cols-2">
        <Link to="/app/forms/submissions" className="card block p-4 hover:bg-brand-50/40">
          <div className="text-sm font-medium text-brand-800">Submissions</div>
          <div className="mt-1 text-sm text-gray-600">
            Forms already filled in, and any left unfinished.
          </div>
        </Link>
        {manager && (
          <Link to="/app/forms/templates" className="card block p-4 hover:bg-brand-50/40">
            <div className="text-sm font-medium text-brand-800">Form templates</div>
            <div className="mt-1 text-sm text-gray-600">
              Build, publish and manage the forms your crew fills in.
            </div>
          </Link>
        )}
      </section>
    </>
  );
}
